import React, { useState, useRef, useContext, useEffect } from 'react';
import { Container, Col, FormGroup, Label, Card, CardBody, CardFooter, Row, CardHeader } from 'reactstrap';
import styles from './CoverageDocuments.module.css';
import useLoadAtTop from '../../components/_ReactHooks/useLoadAtTop';
import { DropDownAutoComplete } from '../../components/inputs/DropDownAutoComplete';
import DocumentTitle from 'react-document-title';
import SimpleReactValidator from 'simple-react-validator';
import { ContractContext } from '../contexts/ContractContext';
import CallLink from '../shared/PhoneNumberUtility/CallLink';
import getClientContent from '../_BrandingProvider/brandContent';
import moment from 'moment';
import { openDocument } from '../Global/DocumentHelper';
import classNames from 'classnames';
import { useHistory } from 'react-router';

const CoverageDocuments = (props) => {
    useLoadAtTop();
    const context = useContext(ContractContext);
    const history = useHistory();
    const [selectedPlanType, setSelectedPlanType] = useState('');
    const [selectedPurchasePlan, setSelectedPurchasePlan] = useState('');
    const [validationFired, setValidationFired] = useState(false);
    const [, forceUpdate] = useState();
    const [selectCoveredItem, setSelectedCoveredItem] = useState('');
    const [selectState, setSelectedState] = useState('');
    const links = [
        { display: 'Disclosure', id: 'disclosures' },
        { display: 'eConsent', id: 'econsent' },
        { display: 'Apple Watch Coverage Certificate', id: 'watch' },
        { display: 'iPad Coverage Certificate', id: 'ipad' },
        { display: 'iPhone Coverage Certificate', id: 'iphone' }
    ];
    const [documentList, setDocumentList] = useState([]);
    const validator = useRef(
        new SimpleReactValidator({
            element: (message) => (
                <div
                    className='errorMsg'
                    aria-live='polite'
                >
                    {message.charAt(0).toUpperCase() + message.slice(1)}
                </div>
            )
        })
    );

    const {
        data: { clientId }
    } = useContext(ContractContext);
    let { phone } = getClientContent(clientId)?.variableText?.customerService;
    const clientTextValues = getClientContent(clientId);
    const title = clientTextValues.variableText.pageTitle;

    const pageTitle = title ? `Coverage Documents | ${title}` : 'Coverage Documents';
    const [state, setState] = useState({
        coverageDocs: [],
        planTypeOptions: [],
        coveredItemOptions: [],
        purchasePlanOptions: [],
        documents: [],
        availablePlans: [],
        states: []
    });
    const coveragePlanType = context.data?.coveragePlanType ?? 'HomeWarranty';
    const [currentPlan, setCurrentPlan] = useState('');
    const [validatePlan, setValidatePlan] = useState(false);

    useEffect(() => {
        if (clientId.toLowerCase() !== 'applestandalone') {
            getCoverageDocs();
        } else {
            fetchStates();
        }
    }, [clientId]);

    const fetchStates = async () => {
        fetch('api/party/states/1/' + clientId)
            .then((res) => res.json())
            .then((data) => {
                setState({
                    states: data.map((s) => {
                        return {
                            data: s.stateId,
                            display: s.name,
                            abbreviation: s.abbreviation
                        };
                    })
                });
            });
    };

    const handleLinkClick = async (event, link) => {
        event.preventDefault();
        try {
            if (documentList.length) {
                const fileName = documentList.filter((file) => file.fileName.toLowerCase().includes(link))[0]?.fileName;
                const responseMedia = await fetch(
                    `api/Document/GetDynamicDocument/2/2/${fileName}/${clientId}/${selectState.abbreviation}`
                );
                const blob = await responseMedia.blob();
                const url = window.URL.createObjectURL(blob);
                window.open(url, '_blank');
            }
        } catch (error) {
            console.error(`failed to open document`, error);
        }
    };

    const getCoverageDocs = async () => {
        fetch(`api/document/GetCoverageDocuments/${clientId}`)
            .then((res) => res.json())
            .then((data) => {
                if (Array.isArray(data)) {
                    let planTypeOptions = [];
                    let coveredItemOptions = [];
                    let documents = [];
                    let availablePlans = [];

                    if (coveragePlanType.toLowerCase() === 'account') {
                        data.map((asset) => {
                            if (
                                !planTypeOptions.find(
                                    (existingAsset) => existingAsset.data === asset.warrantyObjectValue
                                )
                            ) {
                                planTypeOptions.push({
                                    data: asset.warrantyObjectValue,
                                    display: asset.mappedDocumentAssetName
                                });
                            }
                        });
                    } else {
                        data[0]?.assetCoverageModels?.map((asset) => {
                            if (!coveredItemOptions.find((existingAsset) => existingAsset.data === asset.assetId)) {
                                coveredItemOptions.push({
                                    data: asset.assetId,
                                    display: asset.assetName
                                });
                            }
                        });
                        documents = data[0]?.documents;
                        data[0]?.documents?.map((item) => {
                            if (!availablePlans.find((plan) => plan.data === item.description)) {
                                availablePlans.push({
                                    data: item.description,
                                    display: item.description
                                });
                            }
                        });
                    }

                    setState((prev) => ({
                        ...prev,
                        coverageDocs: data,
                        planTypeOptions: sortLists(planTypeOptions),
                        coveredItemOptions: sortLists(coveredItemOptions),
                        documents,
                        availablePlans: sortLists(availablePlans)
                    }));
                }
            });
    };

    const sortLists = (options) => {
        return options.sort((a, b) => a.display.localeCompare(b.display));
    };

    const getDocVersions = (warrObjectValue) => {
        const { coverageDocs } = state;
        const filteredDocs = coverageDocs.filter((x) => x.warrantyObjectValue == warrObjectValue);
        const purchasePlanOptions = filteredDocs.map((x) => {
            return {
                data: x.documentId,
                display: `Purchased on or after ${moment(x.effectiveFrom).format('MMMM DD, YYYY')}`
            };
        });

        setState((prev) => ({
            ...prev,
            purchasePlanOptions
        }));

        if (Array.isArray(purchasePlanOptions)) {
            setSelectedPurchasePlan(purchasePlanOptions[0].data);
        }
    };

    const handlePlanTypeDropdown = (e) => {
        getDocVersions(e);
        setSelectedPlanType(e);
    };

    const handlePurchaseDropDown = (e) => {
        setSelectedPurchasePlan(e);
    };

    const handleSubmit = () => {
        setValidationFired(true);
        if (validator.current.allValid()) {
            openDocument(12, selectedPlanType, selectedPurchasePlan, clientId);
        } else {
            validator.current.showMessages();
            forceUpdate(1);
        }
    };

    const handleViewTermsConditions = () => {
        setValidatePlan(true);
        if (currentPlan) {
            setValidatePlan(false);
            const document = state.documents?.find((item) => item.description === currentPlan);

            openDocument(document?.warrantyObjectTypeId, document?.warrantyObjectValue, document?.documentId, clientId);
        } else {
            validator.current.showMessages();
            forceUpdate(1);
        }
    };

    const handleAssetCoverageOverview = () => {
        setValidationFired(true);
        if (selectCoveredItem) {
            setValidationFired(false);
            const pathName = '/assetcoverageoverview';
            history.push({
                pathname: pathName,
                state: {
                    assetName: getAssetName(selectCoveredItem)
                }
            });
        } else {
            validator.current.showMessages();
            forceUpdate(1);
        }
    };

    const handleCoveredItemDropdown = (e) => {
        setSelectedCoveredItem(e);
    };

    const handleStatesDropdown = async (e) => {
        try {
            const stateSelected = state.states.filter((state) => state.data === e)[0];
            setSelectedState(stateSelected);
            const response = await fetch(`api/document/GetAllFileBytes/2/2/${clientId}/${stateSelected?.abbreviation}`);
            const result = await response.json();
            setDocumentList(result);
        } catch (error) {
            console.error(`failed to get document list`, error);
        }
    };

    const getAssetName = (assetId) => {
        const filteredAssets = state.coveredItemOptions.filter((asset) => asset.data === assetId);
        return filteredAssets[0]?.display;
    };

    const handlePlanSelection = (opt) => {
        setCurrentPlan(opt);
    };

    return (
        <DocumentTitle title={pageTitle}>
            <>
                <div className={styles.pageHeader}>
                    {coveragePlanType?.toLowerCase() === 'account' && (
                        <Container className={styles.pageHeaderContent}>
                            <h1>Terms and Conditions</h1>
                            <p className='mb-0 text-center'>
                                Terms and Condition vary depending on the date of purchase and the type of plan. Choose
                                from the selections below to view and print the appropriate version.
                            </p>
                        </Container>
                    )}
                    {coveragePlanType?.toLowerCase() === 'homewarranty' && (
                        <Container className={styles.pageHeaderContent}>
                            <h1 class='mb-0'>Want to View Your Coverage Details?</h1>
                            <p className='mb-0 text-center'>
                                View the complete terms and conditions of your current plan or review coverage details
                                on individual covered products and home systems.
                            </p>
                        </Container>
                    )}
                    {coveragePlanType?.toLowerCase() === 'standalone' && (
                        <Container className={styles.pageHeaderContent}>
                            <h1 class='mb-0'>AIG Standalone Protection Coverage Documents</h1>
                            <p className='mb-0 text-center'>
                                To view the complete terms and conditions, select the state of the billing address used
                                when you purchased your plan.
                            </p>
                        </Container>
                    )}
                </div>
                {coveragePlanType?.toLowerCase() === 'account' && (
                    <Container>
                        <div className={styles.termsForm}>
                            <Col className='mb-2 p-0'>
                                <FormGroup className={validationFired && !selectedPlanType ? 'form-error' : ''}>
                                    <Label
                                        htmlFor='planType'
                                        className='form-label font-weight-bold'
                                    >
                                        Type of Plan
                                    </Label>
                                    <span
                                        aria-hidden='true'
                                        className='required-indicator'
                                    >
                                        *
                                    </span>
                                    <DropDownAutoComplete
                                        name='planType'
                                        options={state.planTypeOptions}
                                        className='form-control'
                                        aria-required='true'
                                        onChangedValue={handlePlanTypeDropdown}
                                        placeholder={'Please select...'}
                                        id='planType'
                                        defaultValue={selectedPlanType}
                                        Value={selectedPlanType}
                                    />
                                    {validator.current.message('planType', selectedPlanType, 'required', {
                                        messages: { required: 'Type of Plan is required.' }
                                    })}
                                </FormGroup>
                                <FormGroup className={validationFired && !selectedPurchasePlan ? 'form-error' : ''}>
                                    <Label
                                        htmlFor='purchaseDate'
                                        className='form-label font-weight-bold'
                                    >
                                        When Did You Purchase the Plan?
                                    </Label>
                                    <span
                                        aria-hidden='true'
                                        className='required-indicator'
                                    >
                                        *
                                    </span>
                                    <DropDownAutoComplete
                                        name='purchasePlanDate'
                                        options={state.purchasePlanOptions}
                                        className='form-control'
                                        onChangedValue={handlePurchaseDropDown}
                                        placeholder={'Please select...'}
                                        id='purchasePlanDate'
                                        defaultValue={selectedPurchasePlan}
                                        Value={selectedPurchasePlan}
                                    />
                                    {validator.current.message('purchasePlanDate', selectedPurchasePlan, 'required', {
                                        messages: { required: 'Please select a date range.' }
                                    })}
                                </FormGroup>
                            </Col>
                            <Col
                                lg='12'
                                md='12'
                                className='d-flex flex-center mb-2'
                            >
                                <button
                                    className='btn btn-primary mb-2 mt-2'
                                    onClick={handleSubmit}
                                >
                                    View Terms and Conditions
                                </button>
                            </Col>
                            <Col className='text-center'>
                                <p className='mb-0'>Need Help?</p>
                                <p>
                                    <CallLink phone={phone} />
                                </p>
                            </Col>
                        </div>
                    </Container>
                )}
                {coveragePlanType?.toLowerCase() === 'homewarranty' && (
                    <Container className={styles.containerPadding}>
                        <Row className='twoColWrapper'>
                            <Col>
                                <Card className={styles.coverageCard}>
                                    <CardBody className={styles.pageHeaderContent}>
                                        <h4 className={styles.coverageHeader}>Service Plan Terms and Conditions</h4>
                                        <Row>
                                            <FormGroup
                                                className={classNames(
                                                    styles.plan,
                                                    validatePlan && !currentPlan ? 'form-error' : ''
                                                )}
                                            >
                                                <Label
                                                    htmlFor='planName'
                                                    className='form-label'
                                                >
                                                    Type of Plan
                                                </Label>
                                                <span
                                                    aria-hidden='true'
                                                    className='required-indicator'
                                                >
                                                    *
                                                </span>
                                                <DropDownAutoComplete
                                                    name='planName'
                                                    id='planName'
                                                    options={state.availablePlans}
                                                    className='form-control'
                                                    aria-required='true'
                                                    onChangedValue={handlePlanSelection}
                                                    placeholder={'Please select...'}
                                                    defaultValue={currentPlan}
                                                    Value={currentPlan}
                                                />
                                                {validatePlan
                                                    && validator.current.message('planName', currentPlan, 'required', {
                                                        messages: { required: 'Type of plan is required.' }
                                                    })}
                                            </FormGroup>
                                        </Row>
                                        <Row>
                                            <FormGroup>
                                                <button
                                                    className='btn btn-primary'
                                                    onClick={handleViewTermsConditions}
                                                >
                                                    Terms and Conditions
                                                </button>
                                            </FormGroup>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col>
                                <Card className={styles.coverageCard}>
                                    <CardBody className={styles.pageHeaderContent}>
                                        <h4 className={styles.coverageHeader}>Coverage Details by Covered Item</h4>
                                        <p className='mb-3 text-center'>
                                            Select a covered product or home system to view coverage details and
                                            exclusions.
                                        </p>
                                        <Row>
                                            <FormGroup
                                                className={classNames(
                                                    styles.coverageItemDropdown,
                                                    validationFired && !selectCoveredItem ? 'form-error' : ''
                                                )}
                                            >
                                                <Label
                                                    htmlFor='coverageItem'
                                                    className={classNames('form-label', styles.coveredItemLabel)}
                                                >
                                                    Covered Item
                                                </Label>
                                                <span
                                                    aria-hidden='true'
                                                    className='required-indicator'
                                                >
                                                    *
                                                </span>
                                                <DropDownAutoComplete
                                                    name='coverageItem'
                                                    id='covItemType'
                                                    options={state.coveredItemOptions}
                                                    className='form-control'
                                                    aria-required='true'
                                                    onChangedValue={handleCoveredItemDropdown}
                                                    placeholder={'Please select...'}
                                                    defaultValue={selectCoveredItem}
                                                    Value={selectCoveredItem}
                                                />
                                                {validationFired
                                                    && validator.current.message(
                                                        'covItemType',
                                                        selectCoveredItem,
                                                        'required',
                                                        { messages: { required: 'Covered Item is required.' } }
                                                    )}
                                            </FormGroup>
                                        </Row>
                                        <Row>
                                            <FormGroup>
                                                <button
                                                    className='btn btn-primary mb-2 mt-2'
                                                    onClick={handleAssetCoverageOverview}
                                                >
                                                    View Details
                                                </button>
                                            </FormGroup>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                )}
                {coveragePlanType?.toLowerCase() === 'standalone' && (
                    <Container className={styles.containerPadding}>
                        <Row>
                            <Col>
                                <Card className={styles.coverageCardBundled}>
                                    <CardBody className={styles.pageHeaderContent}>
                                        <Row>
                                            <FormGroup
                                                className={classNames(
                                                    styles.coverageItemDropdown,
                                                    validationFired && !selectCoveredItem ? 'form-error' : ''
                                                )}
                                            >
                                                <Label
                                                    htmlFor='coverageItem'
                                                    className={classNames('form-label', styles.coveredItemLabel)}
                                                >
                                                    State
                                                </Label>
                                                <span
                                                    aria-hidden='true'
                                                    className='required-indicator'
                                                >
                                                    *
                                                </span>
                                                <DropDownAutoComplete
                                                    name='states'
                                                    id='stateType'
                                                    aria-label='Select State'
                                                    className='form-control'
                                                    options={state.states}
                                                    aria-required='true'
                                                    placeholder={'Please select...'}
                                                    onChangedValue={handleStatesDropdown}
                                                    defaultValue={selectState?.data}
                                                    Value={selectState?.data}
                                                />
                                                {validator.current.message(
                                                    'stateType',
                                                    selectState?.display,
                                                    'required',
                                                    { messages: { required: 'State is required.' } }
                                                )}
                                            </FormGroup>
                                        </Row>
                                        <Row>
                                            {selectState && (
                                                <p className={styles.linksCoverage}>
                                                    <h2 class='h4 mb-0'>{selectState.display} Coverage Documents</h2>
                                                    <div className={styles.linksContainer}>
                                                        {links.map((link) => (
                                                            <a
                                                                href='#'
                                                                className={styles.linksBundled}
                                                                onClick={(e) => handleLinkClick(e, link.id)}
                                                            >
                                                                {link.display}
                                                            </a>
                                                        ))}
                                                    </div>
                                                </p>
                                            )}
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                )}
            </>
        </DocumentTitle>
    );
};

export default CoverageDocuments;
